import { AbstractControl } from '@angular/forms';
import { KeyValue, PremiumStyle } from '@approot/models';
import { Description } from '@approot/services/rfq/rfq.types';
import { dvSchemas } from '@approot/utilities';
import { ProviderMultiHedge, ProviderQuote } from '../provider-quote.model';

type PremiumStyleFormat = 'pips' | 'percent';

export type Action = dvSchemas['ProviderQuoteEvent']['action'];
export type QuoteStatus =
  dvSchemas['ProviderQuoteEvent']['quote']['quoteStatus'];

export type ActionOrStatus = Action | QuoteStatus;

export type Premium = {
  bid: KeyValue;
  ask: KeyValue;
};

export type Vega = {
  currency: string;
  amount: number;
};

export type LegResult = {
  volBid: number;
  volAsk: number;
  forward: number;
};

export type PortfolioResults = {
  premium: Premium;
  pv: {
    mid: KeyValue;
    bsMid: KeyValue;
  };
  spotDelta: KeyValue;
  forwardDelta: KeyValue;
  vega: Vega;
  spotPremium?: Record<string, Record<'ask' | 'mid' | 'bid', number>>;
};

export interface PremiumInStyleInfo {
  label: { bid: string; offer: string };
  format: { bid: string; offer: string };
  liveValue: { bid: boolean; offer: boolean };
  latestValue: { bid: string; offer: string };
}

export interface PremiumInStyleCurrentlyQuoted {
  bid: {
    format: PremiumStyleFormat;
    value: string;
  };
  offer: {
    format: PremiumStyleFormat;
    value: string;
  };
}

export interface CashPremiumInfo {
  label: { bid: string; offer: string };
  direction: { bid: ReceiveOrPay; offer: ReceiveOrPay };
  liveValue: { bid: boolean; offer: boolean };
  latestValue: { bid: string; offer: string };
}

export interface VolBasedRfqOptionPriceV2Request {
  task: 'volBasedRfqOptionPrice_v2';
  premiumDate: string;
  premiumValue: string;
  premiumCurrency: string;
  hedgeMode: 'None';
  hedges: VolBasedRfqOptionPriceV2Hedge[];
  products: KeyValue[];
  requestId: number;
  spotAsk?: string;
  spotBid?: string;
  comment?: string;
}

export interface VolBasedRfqOptionPriceV2Hedge {
  productType?: string;
  settlementDate?: string;
  expiryDate?: string;
  strike?: number;
  notional?: number;
  position?: 'Buy' | 'Sell';
}

export interface FranchiseCredit {
  conversionRateFromPremiumCurrencyIntoFranchiseCreditCurrency: number;
  franchiseCreditCurrency: string;
  franchiseCreditInFranchiseCreditCurrency: number;
  franchiseCreditInPremiumCurrency: number;
}

export interface VolBasedRfqOptionPriceV2Response {
  premiumDate: string;
  premiumValue: string;
  premiumCurrency: string;
  hedgeMode: string;
  premiumStyle: PremiumStyle;
  portfolioResult: any;
  description: Description;
  results: any;
  timestamp: any;
  franchiseCredit: FranchiseCredit;
}

export type PriceResult = {
  isLastStreamingMessage?: boolean;
  uuid?: string;
  portfolioResults: PortfolioResults;
  resultPerLeg: LegResult[];
  marketData: {
    spot: KeyValue;
  };
  hedge?: ProviderMultiHedge[];
  timestamp: string;
  statusCode: number;
};

export type ClientPriceResults = {
  portfolioResult: {
    forwardPremium: {
      ask: Record<string, number>;
      bid: Record<string, number>;
    };
    spotPremium: {
      ask: Record<string, number>;
      bid: Record<string, number>;
    };
  };
  premiumCurrency: string;
  premiumValue: 'Spot' | 'Forward';
  premiumStyle: PremiumStyle;
};

export type ClientPriceDisplayResults = {
  premium: {
    bid: number;
    ask: number;
    askType: 'Pay' | 'Receive';
    bidType: 'Pay' | 'Receive';
  };
  premiumCurrency: string;
  premiumStyle: PremiumStyle;
};

export type DeltaType = 'Spot' | 'Forward';

export const ActionMessage: Partial<{ [key in ActionOrStatus]: string }> = {
  SUBMITTED: 'A new RFQ has been submitted',
  CANCELLED: 'The RFQ has been cancelled',
  Declined: 'The RFQ has been declined',
  ConfirmQuoteSubmit:
    'The quote has been submitted. Waiting for a client to accept.',
  ClientAccept: 'The client has accepted the quote',
  ConfirmRFEAccept: 'The RFE has been accepted',
  PickedUp: 'The RFQ has been picked up',
  Released: 'The RFQ has been released for another user',
  ConfirmWithdraw: 'ConfirmWithdraw',
  ConfirmRFEDecline: 'ConfirmRFEDecline',
  QuoteDelay: 'QuoteDelay',
  RFEExpired: 'The RFE has expired',
  Unsupported: 'Unsupported',
  LoggedOff: 'LoggedOff',
  DealCancelled: 'DealCancelled',
  Expired: 'Expired',
  RFESuspended: 'RFESuspended',
  ConfirmRFEError: 'ConfirmRFEError'
};

export const ActionStatusMap: Partial<{
  [key in ActionOrStatus]: ActionOrStatus;
}> = {
  CANCELLED: 'Cancelled',
  SUBMITTED: 'Submitted',
  Unquoted: 'Submitted'
};

export const DeadStatuses: ActionOrStatus[] = [
  'CANCELLED',
  'Declined',
  'Released',
  'ConfirmRFEAccept'
];

export type VolBasedRisks =
  | 'spotDelta'
  | 'brokerSpotDelta'
  | 'gamma'
  | 'brokerGamma';

export type AllVolBasedRisksRecords = Partial<
  Record<VolBasedRisks, number[]> & Record<`${VolBasedRisks}Total`, number>
>;

export const VolBasedRiskKeys: VolBasedRisks[] = [
  'spotDelta',
  'brokerSpotDelta',
  'gamma',
  'brokerGamma'
];

export type PremiumBasedRisks = 'spotDelta' | 'spotDeltaInPercent';

export type AllPremiumBasedRisksRecords = Partial<
  Record<PremiumBasedRisks, number[]> &
    Record<`${PremiumBasedRisks}Total`, number>
>;

export const PremiumBasedRiskKeys: PremiumBasedRisks[] = [
  'spotDelta',
  'spotDeltaInPercent'
];

export type FormDef = {
  premiumAmountOffer: AbstractControl;
  premiumAmountBid: AbstractControl;
  volBid: AbstractControl;
  volOffer: AbstractControl;
  hedgeRateBid: AbstractControl;
  hedgeRateOffer: AbstractControl;
  hedgeBid: AbstractControl;
  hedgeOffer: AbstractControl;
  bidOption: AbstractControl;
  offerOption: AbstractControl;
  hedgeBidOption: AbstractControl;
  hedgeOfferOption: AbstractControl;
  marginBid: AbstractControl;
  marginOffer: AbstractControl;
  bidPremiumInStyle: AbstractControl;
  offerPremiumInStyle: AbstractControl;
  cashPremiumBid: AbstractControl;
  cashPremiumOffer: AbstractControl;
};

export type FormKey = keyof FormDef;
export type Hedge = ProviderQuote['request']['hedge'];
export type ReceiveOrPay = 'clientReceives' | 'clientPays';
export type PriceStreamingState = 'STREAMING' | 'MANUAL' | 'UNAVAILABLE';

export type VolsError = {
  direction: '<' | '>';
  legIndex: number;
};

export type LegInfo = {
  inverted: boolean;
  productType: string;
  isSingleVolProduct: boolean;
  isSimpleLeg: boolean;
};

export type ActiveClass =
  | 'active-normal'
  | 'inactive-normal'
  | 'active-volbased'
  | 'inactive-volbased'
  | '';

export interface PremiumInStyle {
  value: number;
  format?: PremiumStyleFormat;
}

export interface PremiumCash {
  label: string;
  value: number;
  direction: ReceiveOrPay;
}
